<template>
  <el-form ref="deviceForm" :model="formData" :rules="rules" label-width="100px" class="device-form">
    <!-- 流速计数据上传方式提示 -->
    <el-alert
      v-if="formData.device_type === 'FLOWMETER'"
      title="流速计数据由设备直接上传至后端接口，无需配置连接类型、主机地址和端口"
      type="info"
      :closable="false"
      show-icon
      style="margin-bottom: 20px;"
    ></el-alert>

    <!-- 将表单字段分为两列显示 -->
    <div class="form-row">
      <el-form-item label="设备名称" prop="name" class="form-col">
        <el-input v-model="formData.name" placeholder="请输入设备名称"></el-input>
      </el-form-item>
      
      <el-form-item label="设备编码" prop="device_code" class="form-col">
        <el-input v-model="formData.device_code" :placeholder="deviceCodePlaceholder"></el-input>
        <div v-if="formData.device_type === 'FLOWMETER'" class="el-form-item__help" style="color: #909399; font-size: 12px; line-height: 1.5; margin-top: 4px;">
          请填写设备序列号（即设备机身上的 ST 编号），系统通过此编码匹配上送数据
        </div>
      </el-form-item>
    </div>
    
    <div class="form-row">
      <el-form-item label="设备类型" prop="device_type" class="form-col">
        <el-select v-model="formData.device_type" placeholder="请选择设备类型" class="w-full">
          <el-option
            v-for="item in deviceTypes"
            :key="item.value"
            :label="item.label"
            :value="item.value">
          </el-option>
        </el-select>
      </el-form-item>
      
      <el-form-item label="状态" prop="status" class="form-col">
        <el-select v-model="formData.status" placeholder="请选择设备状态" class="w-full">
          <el-option label="在线" value="online"></el-option>
          <el-option label="离线" value="offline"></el-option>
          <el-option label="警告" value="warning"></el-option>
          <el-option label="维护" value="maintenance"></el-option>
        </el-select>
      </el-form-item>
    </div>
    
    <div class="form-row">
      <el-form-item label="设备子类型" prop="type" class="form-col">
        <el-select v-model="formData.type" placeholder="请选择设备子类型" class="w-full">
          <el-option
            v-for="item in availableSubtypes"
            :key="item.value"
            :label="item.label"
            :value="item.value">
          </el-option>
        </el-select>
      </el-form-item>

      <el-form-item label="启用状态" prop="enabled" class="form-col" v-if="formData.device_type !== 'FLOWMETER'">
        <el-select v-model="formData.enabled" placeholder="请选择启用状态" class="w-full">
          <el-option :label="'启用'" :value="1"></el-option>
          <el-option :label="'禁用'" :value="0"></el-option>
        </el-select>
      </el-form-item>
    </div>
    
    <div class="form-row" v-if="formData.device_type !== 'FLOWMETER'">
      <el-form-item label="连接类型" prop="connection_type" class="form-col">
        <el-select v-model="formData.connection_type" placeholder="请选择连接类型" class="w-full">
          <el-option label="TCP" value="TCP"></el-option>
          <el-option label="UDP" value="UDP"></el-option>
          <el-option label="串口" value="SERIAL"></el-option>
          <el-option label="HTTP" value="HTTP"></el-option>
          <el-option label="HTTPS" value="HTTPS"></el-option>
        </el-select>
      </el-form-item>
      
      <el-form-item label="启用状态" prop="enabled" class="form-col" v-if="formData.device_type === 'FLOWMETER'">
        <el-select v-model="formData.enabled" placeholder="请选择启用状态" class="w-full">
          <el-option :label="'启用'" :value="1"></el-option>
          <el-option :label="'禁用'" :value="0"></el-option>
        </el-select>
      </el-form-item>
    </div>
    
    <div class="form-row" v-if="formData.device_type !== 'FLOWMETER'">
      <el-form-item label="主机地址" prop="host" class="form-col">
        <el-input v-model="formData.host" placeholder="请输入设备IP地址或域名"></el-input>
      </el-form-item>
      
      <el-form-item label="端口" prop="port" class="form-col">
        <el-input-number v-model="formData.port" :min="0" :max="65535" class="w-full"></el-input-number>
      </el-form-item>
    </div>
    
    <el-form-item label="地理坐标">
      <div class="flex space-x-4 coord-inputs">
        <el-form-item prop="longitude" class="mb-0 flex-1">
          <el-input v-model.number="formData.longitude" placeholder="经度" clearable>
            <template slot="prepend">经度</template>
          </el-input>
        </el-form-item>
        <el-form-item prop="latitude" class="mb-0 flex-1">
          <el-input v-model.number="formData.latitude" placeholder="纬度" clearable>
            <template slot="prepend">纬度</template>
          </el-input>
        </el-form-item>
        <el-button type="primary" @click="openMapDialog" icon="el-icon-map-location">地图选点</el-button>
      </div>
    </el-form-item>
    
    <el-form-item label="设备位置" prop="location">
      <el-input v-model="formData.location" placeholder="请输入设备位置描述，例如：东侧桥墩1"></el-input>
    </el-form-item>

    <!-- NVR 视频配置（仅 NVR 监控类型显示） -->
    <template v-if="formData.device_type === 'CAMERA'">
      <el-divider content-position="left">NVR 视频配置</el-divider>
      <div class="form-row">
        <el-form-item label="RTSP 地址" class="form-col">
          <el-input v-model="nvr.rtsp_host" placeholder="默认同主机地址"></el-input>
        </el-form-item>
        <el-form-item label="RTSP 端口" class="form-col">
          <el-input-number v-model="nvr.rtsp_port" :min="0" :max="65535" :controls="false" :precision="0" class="w-full" placeholder="空=未接入"></el-input-number>
        </el-form-item>
      </div>
      <div class="form-row">
        <el-form-item label="账号" class="form-col" required>
          <el-input v-model="nvr.username" placeholder="admin"></el-input>
        </el-form-item>
        <el-form-item label="密码" class="form-col" required>
          <el-input v-model="nvr.password" show-password placeholder="NVR 密码"></el-input>
        </el-form-item>
      </div>
      <div class="form-row">
        <el-form-item v-if="!nvr.id" label="通道数" class="form-col">
          <el-input-number v-model="nvr.channel_count" :min="1" :max="64" class="w-full"></el-input-number>
        </el-form-item>
        <el-form-item label="备注" class="form-col">
          <el-input v-model="nvr.remark" placeholder="NAT/FRP 映射说明等"></el-input>
        </el-form-item>
      </div>
      <el-form-item v-else>
        <el-alert type="warning" :closable="false" show-icon
          title="本设备编码未关联到视频配置"
          description="上方「主机地址/端口」按本设备记录显示（RTSP 服务）；保存时将自动创建视频配置（NVR 编码 = 设备编码），最长 30s 内自动生效。若同一 NVR 已另有设备行，建议到该行维护。" />
      </el-form-item>
    </template>
    
    <el-form-item label="串口参数" prop="serial_params" v-if="formData.connection_type === 'SERIAL'">
      <el-input type="textarea" v-model="formData.serial_params" :rows="3" placeholder="请输入串口参数，JSON格式"></el-input>
    </el-form-item>
    
    <!-- 设备扩展参数配置 - 手风琴样式 -->
    <div v-if="showExtendParamsConfig" class="extend-params-accordion">
      <div class="accordion-header" @click="toggleExtendParamsPanel">
        <div class="header-content">
          <i class="el-icon-setting mr-2 text-blue-600"></i>
          <span class="header-title">扩展参数配置</span>
          <el-tag v-if="hasExtendParams" size="mini" type="success" class="ml-2">已配置</el-tag>
          <el-tag v-else size="mini" type="info" class="ml-2">未配置</el-tag>
        </div>
        <i 
          :class="[
            'accordion-arrow',
            'el-icon-arrow-right',
            { 'accordion-arrow-expanded': extendParamsExpanded }
          ]"
        ></i>
      </div>
      
      <el-collapse-transition>
        <div v-show="extendParamsExpanded" class="accordion-content">
          <device-extend-params-config
            ref="extendParamsConfig"
            :deviceData="formData"
            :deviceType="formData.device_type"
            :deviceSubtype="formData.type || 'default'"
            :showActions="false"
            @change="handleExtendParamsChange"
          />
        </div>
      </el-collapse-transition>
    </div>
    
    <el-form-item class="text-right">
      <el-button @click="$emit('cancel')">取消</el-button>
      <el-button type="primary" @click="submitForm">确定</el-button>
    </el-form-item>

    <!-- 使用地图选点组件 -->
    <map-picker
      :visible.sync="mapDialogVisible"
      :initial-coordinates="tempCoords"
      @confirm="handleCoordinatesConfirm"
      @closed="handleMapDialogClosed"
    />
  </el-form>
</template>

<script>
import { 
  DEVICE_TYPES,
  DEVICE_SUBTYPES
} from '@/config/device-constants'

// 引入地图选点组件
import MapPicker from '@/components/common/MapPicker.vue'
// 引入设备扩展参数配置组件
import DeviceExtendParamsConfig from '@/components/device/DeviceExtendParamsConfig.vue'
// 引入设备默认参数API
import deviceDefaultParamsAPI from '@/api/device-default-params.js'
import videoAPI from '@/api/video'

export default {
  name: 'DeviceForm',
  components: {
    MapPicker,
    DeviceExtendParamsConfig
  },
  props: {
    deviceData: {
      type: Object,
      default: () => ({})
    },
    bridgeId: {
      type: [Number, String],
      required: true,
      validator: value => !isNaN(Number(value))
    }
  },
  data() {
    return {
      formData: {
        name: '',
        device_code: '',
        device_type: '',
        type: 'default',
        connection_type: 'TCP',
        bridgeId: this.bridgeId ? Number(this.bridgeId) : null,
        host: '',
        port: '',
        username: '',
        password: '',
        location: '',
        longitude: '',
        latitude: '',
        status: 'offline',
        enabled: 1,
        serial_params: '',
        description: '',
        extend_params: ''
      },
      deviceTypes: DEVICE_TYPES,
      deviceSubtypes: DEVICE_SUBTYPES,
      bridges: [],
      loading: false,
      mapDialogVisible: false,
      tempCoords: {
        longitude: 121.473701,
        latitude: 31.230416
      },
      defaultParamsTemplate: null, // 默认参数模板
      showExtendParamsConfig: false, // 是否显示扩展参数配置
      extendParamsExpanded: false, // 扩展参数面板是否展开
      checkVisibilityTimer: null, // 防抖定时器
      paramsTemplateCache: new Map(), // 参数模板缓存
      nvr: {
        id: null, rtsp_host: '', rtsp_port: null, username: 'admin', password: '',
        channel_count: 8, remark: '', channel_total: null
      },
      nvrLoadedKey: null
    }
  },
  computed: {
    // 根据选择的设备类型显示对应的子类型选项
    availableSubtypes() {
      if (!this.formData.device_type) {
        return [{ value: 'default', label: '默认' }]
      }
      return this.deviceSubtypes[this.formData.device_type] || [{ value: 'default', label: '默认' }]
    },

    // 设备编码输入框的动态提示文字
    deviceCodePlaceholder() {
      if (this.formData.device_type === 'FLOWMETER') {
        return '请输入设备序列号（ST 编号）'
      }
      return '请输入设备编码'
    },

    // 动态验证规则：流速计不需要 host/port
    rules() {
      const isFlowmeter = this.formData.device_type === 'FLOWMETER'
      const rules = {
        name: [
          { required: true, message: '请输入设备名称', trigger: 'blur' },
          { min: 2, max: 50, message: '长度在 2 到 50 个字符', trigger: 'blur' }
        ],
        device_code: [
          { required: true, message: '请输入设备编码', trigger: 'blur' }
        ],
        device_type: [
          { required: true, message: '请选择设备类型', trigger: 'change' }
        ],
        bridgeId: [
          { required: true, message: '请选择所属桥梁', trigger: 'change' }
        ]
      }
      if (!isFlowmeter) {
        rules.host = [
          { required: true, message: '请输入主机地址', trigger: 'blur' }
        ]
        rules.port = [
          { required: true, message: '请输入端口号', trigger: 'blur' },
          { type: 'number', message: '端口号必须为数字', trigger: 'blur', transform: value => Number(value) }
        ]
      }
      return rules
    },
    
    // 检查是否有扩展参数配置
    hasExtendParams() {
      if (!this.formData.extend_params) {
        return false
      }
      try {
        const params = JSON.parse(this.formData.extend_params)
        return Object.keys(params).length > 0
      } catch {
        return false
      }
    }
  },
  watch: {
    // 监听设备类型变化，自动重置子类型为第一个选项
    'formData.device_type': {
      handler(newType) {
        if (newType && this.deviceSubtypes[newType]) {
          // 设置为第一个可用的子类型
          this.formData.type = this.deviceSubtypes[newType][0].value
        }
        // 检查扩展参数配置显示（防抖）
        this.debouncedCheckExtendParamsConfigVisibility()
      }
    },
    // 监听设备子类型变化
    'formData.type': {
      handler() {
        this.debouncedCheckExtendParamsConfigVisibility()
      }
    },
    deviceData: {
      handler(newVal) {
        this.nvrLoadedKey = null
        this.nvr = { id: null, rtsp_host: '', rtsp_port: undefined, username: 'admin', password: '', channel_count: 8, remark: '', channel_total: null }
        if (newVal && Object.keys(newVal).length > 0) {
          this.formData = { ...this.formData, ...newVal };
          // 转换为数字类型的属性
          if (this.formData.port) this.formData.port = Number(this.formData.port);
          if (this.formData.enabled) this.formData.enabled = Number(this.formData.enabled);
          if (this.formData.longitude) this.formData.longitude = Number(this.formData.longitude);
          if (this.formData.latitude) this.formData.latitude = Number(this.formData.latitude);
        }
        // 检查扩展参数配置显示（防抖）
        this.debouncedCheckExtendParamsConfigVisibility()
        if (this.formData.device_type === 'CAMERA' && this.formData.device_code) {
          this.loadNvrByCode()
        }
      },
      immediate: true,
      deep: true
    },
    bridgeId: {
      handler(newVal) {
        // 确保bridgeId存储为数字类型
        this.formData.bridgeId = Number(newVal);
      },
      immediate: true
    }
  },
  created() {
    if (this.formData.device_type === 'CAMERA' && this.formData.device_code) {
      this.loadNvrByCode()
    }
  },
  beforeDestroy() {
    // 清理防抖定时器
    if (this.checkVisibilityTimer) {
      clearTimeout(this.checkVisibilityTimer)
      this.checkVisibilityTimer = null
    }
  },
  methods: {
    submitForm() {
      this.$refs.deviceForm.validate(valid => {
        if (valid) {
          if (this.formData.device_type === 'CAMERA') {
            if (!this.nvr.username || !this.nvr.password) {
              this.$message.warning('NVR 视频配置：账号/密码必填')
              return false
            }
          }
          const formData = { ...this.formData };
          if (this.formData.device_type === 'CAMERA') {
            formData.nvr = {
              id: this.nvr.id,
              rtsp_host: this.nvr.rtsp_host,
              rtsp_port: this.nvr.rtsp_port ? Number(this.nvr.rtsp_port) : null,
              username: this.nvr.username,
              password: this.nvr.password,
              channel_count: this.nvr.channel_count,
              remark: this.nvr.remark
            }
          }
          
          // 处理数据，确保数值类型正确
          if (formData.port) formData.port = Number(formData.port);
          if (formData.enabled) formData.enabled = Number(formData.enabled);
          if (formData.longitude) formData.longitude = Number(formData.longitude);
          if (formData.latitude) formData.latitude = Number(formData.latitude);
          // 确保bridgeId为数字类型
          if (formData.bridgeId) formData.bridgeId = Number(formData.bridgeId);
          
          // 传递表单数据到父组件
          this.$emit('submit', formData);
        } else {
          return false;
        }
      });
    },
    resetForm() {
      this.$refs.deviceForm.resetFields();
      this.nvr = { id: null, rtsp_host: '', rtsp_port: undefined, username: 'admin', password: '', channel_count: 8, remark: '', channel_total: null }
    },
    async loadNvrByCode() {
      if (this.formData.device_type !== 'CAMERA' || !this.formData.device_code || !this.bridgeId) return
      const key = this.bridgeId + '|' + this.formData.device_code
      if (this.nvrLoadedKey === key) return
      try {
        const r = await videoAPI.cfgListNvr({ bridgeId: this.bridgeId })
        const list = r.data || []
        const code = String(this.formData.device_code)
        const port = Number(this.formData.port) || null
        const m = list.find(x => String(x.nvr_code) === code)
          || list.find(x => x.name === code)
          || list.find(x => port && Number(x.web_port) === port)
          || list.find(x => port && Number(x.rtsp_port) === port)
          || null
        if (m) {
          this.nvr.id = m.id
          this.nvr.rtsp_host = m.rtsp_host || ''
          this.nvr.rtsp_port = m.rtsp_port || null
          this.nvr.username = m.username || 'admin'
          this.nvr.remark = m.remark || ''
          this.nvr.channel_total = m.channel_total
          // 不再覆盖基础区主机地址/端口，保留 device_tb 自身记录（语义：本行设备的服务地址）
          try {
            const s = await videoAPI.cfgNvrSecret(m.id)
            this.nvr.password = s.data.password
          } catch (e) { /* 无权限则留空 */ }
        } else {
          this.nvr = { id: null, rtsp_host: '', rtsp_port: undefined, username: 'admin', password: '', channel_count: 8, remark: '', channel_total: null }
        }
        this.nvrLoadedKey = key
      } catch (e) { /* 列表不可达时静默 */ }
    },
    // 打开地图选择对话框
    openMapDialog() {
      this.tempCoords = {
        longitude: this.formData.longitude || 121.473701,
        latitude: this.formData.latitude || 31.230416
      };
      this.mapDialogVisible = true;
    },
    closeMapDialog() {
      this.mapDialogVisible = false;
    },
    handleMapDialogClosed() {
      // 处理地图对话框关闭后的逻辑
    },
    handleCoordinatesConfirm(coordinates) {
      this.formData.longitude = coordinates.longitude;
      this.formData.latitude = coordinates.latitude;
      this.mapDialogVisible = false;
    },
    
    /**
     * 检查是否显示扩展参数配置
     */
    async checkExtendParamsConfigVisibility() {
      if (!this.formData.device_type) {
        this.showExtendParamsConfig = false
        return
      }
      
      const deviceType = this.formData.device_type
      const deviceSubtype = this.formData.type || 'default'
      const cacheKey = `${deviceType}-${deviceSubtype}`
      
      // 检查缓存
      if (this.paramsTemplateCache.has(cacheKey)) {
        console.log(`DeviceForm: 缓存命中 - ${cacheKey}`)
        const cachedResult = this.paramsTemplateCache.get(cacheKey)
        this.defaultParamsTemplate = cachedResult.template
        this.showExtendParamsConfig = cachedResult.hasTemplate
        
        // 如果是新设备或没有扩展参数，自动展开面板
        if (cachedResult.hasTemplate && !this.hasExtendParams) {
          this.extendParamsExpanded = true
        }
        return
      }
      
      try {
        console.log(`DeviceForm: API调用 - ${cacheKey}`)
        const response = await deviceDefaultParamsAPI.getByDeviceTypeAndSubtype(deviceType, deviceSubtype)
        
        if (response.code === 200 && response.data) {
          // 缓存结果
          this.paramsTemplateCache.set(cacheKey, {
            template: response.data,
            hasTemplate: true
          })
          
          this.defaultParamsTemplate = response.data
          this.showExtendParamsConfig = true
          
          // 如果是新设备或没有扩展参数，自动展开面板
          if (!this.hasExtendParams) {
            this.extendParamsExpanded = true
          }
        } else {
          // 缓存空结果
          this.paramsTemplateCache.set(cacheKey, {
            template: null,
            hasTemplate: false
          })
          
          this.defaultParamsTemplate = null
          this.showExtendParamsConfig = false
        }
      } catch (error) {
        console.error('检查默认参数模板失败:', error)
        
        // 缓存错误结果
        this.paramsTemplateCache.set(cacheKey, {
          template: null,
          hasTemplate: false
        })
        
        this.defaultParamsTemplate = null
        this.showExtendParamsConfig = false
      }
    },
    
    /**
     * 处理扩展参数变更
     */
    handleExtendParamsChange(extendParamsJson) {
      this.formData.extend_params = extendParamsJson
    },
    
    /**
     * 防抖版本的检查扩展参数配置显示方法
     */
    debouncedCheckExtendParamsConfigVisibility() {
      // 清除之前的定时器
      if (this.checkVisibilityTimer) {
        clearTimeout(this.checkVisibilityTimer)
        console.log('DeviceForm: 防抖 - 取消上一次检查')
      }
      
      console.log('DeviceForm: 防抖 - 延迟100ms后执行检查')
      
      // 设置新的定时器，延迟100ms执行
      this.checkVisibilityTimer = setTimeout(() => {
        console.log('DeviceForm: 防抖 - 开始执行检查')
        this.checkExtendParamsConfigVisibility()
      }, 100)
    },
    
    /**
     * 切换扩展参数面板的展开状态
     */
    toggleExtendParamsPanel() {
      this.extendParamsExpanded = !this.extendParamsExpanded
    }
  }
}
</script>

<style scoped>
.device-form {
  max-width: 100%;
}

.w-full {
  width: 100%;
}

.flex {
  display: flex;
}

.flex-1 {
  flex: 1;
}

.space-x-4 > * + * {
  margin-left: 1rem;
}

/* 两列布局样式 */
.form-row {
  display: flex;
  margin: 0 -10px;
}

.form-col {
  width: 50%;
  padding: 0 10px;
  box-sizing: border-box;
}

/* 坐标输入区域样式 */
.coord-inputs {
  display: flex;
  align-items: center;
}

/* 扩展参数配置区域样式 - 手风琴样式 */
.extend-params-accordion {
  margin-top: 20px;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  overflow: hidden;
  background-color: #ffffff;
}

.accordion-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-bottom: 1px solid #e4e7ed;
  cursor: pointer;
  transition: all 0.3s ease;
  user-select: none;
}

.accordion-header:hover {
  background: linear-gradient(135deg, #f1f5f9 0%, #cbd5e1 100%);
}

.header-content {
  display: flex;
  align-items: center;
  flex: 1;
}

.header-title {
  font-weight: 500;
  color: #374151;
  font-size: 14px;
}

.accordion-arrow {
  font-size: 16px;
  color: #6b7280;
  transition: transform 0.3s ease;
}

.accordion-arrow-expanded {
  transform: rotate(90deg);
}

.accordion-content {
  padding: 20px;
  background-color: #fafbfc;
  border-top: 1px solid #f3f4f6;
}

.text-blue-600 {
  color: #2563eb;
}

.text-gray-600 {
  color: #606266;
}

.mr-2 {
  margin-right: 0.5rem;
}

.ml-2 {
  margin-left: 0.5rem;
}
</style> 